/**
 * Vista 12 · Política y plazos.
 *
 * `NEG-6` en pantalla: un cambio de plazo **afecta solo a las consultas que se
 * abran desde ahora**. Las que ya están en curso conservan el suyo, porque el
 * plazo se congela al emitir y está impreso en el oficio que la entidad tiene
 * en la mano. La vista lo dice con el número exacto de consultas afectadas.
 */
import { useEffect, useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@mpfn/ui'
import { Callout, FormField } from '@mpfn/patterns'
import { useGuardarPolitica, usePolitica } from '../api/consultas.js'
import { useCatalogos } from '../api/catalogos.js'
import type { Canal, Politica as PoliticaVigente } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { Casilla } from '../componentes/Conmutadores.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { fecha } from '../lib/formato.js'

const MINIMO_MOTIVO = 10

export function Politica() {
  const politica = usePolitica()

  return (
    <Pagina ancho="formulario"
      titulo="Política de fiscalización"
      descripcion="Cuántas veces se insiste, cuánto se espera por cada canal y qué canales están habilitados."
    >
      <Datos estado={politica}>{(datos) => <Formulario vigente={datos} />}</Datos>
    </Pagina>
  )
}

function Formulario({ vigente }: { vigente: PoliticaVigente }) {
  const catalogos = useCatalogos()
  const guardar = useGuardarPolitica()

  const [minimo, setMinimo] = useState(String(vigente.minimoRequerimientos))
  const [maximo, setMaximo] = useState(String(vigente.maximoRequerimientos))
  const [exigeEntrega, setExigeEntrega] = useState(vigente.exigeEntregaParaDerivar)
  const [porcentaje, setPorcentaje] = useState(String(vigente.porcentajeMinimoSorteo))
  const [maximoSemestre, setMaximoSemestre] = useState(String(vigente.maximoLegajosSemestre))
  const [plazos, setPlazos] = useState<Record<Canal, number>>({ ...vigente.plazosPorCanal })
  const [habilitados, setHabilitados] = useState<readonly Canal[]>(vigente.canalesHabilitados)
  const [motivo, setMotivo] = useState('')
  const [enCurso, setEnCurso] = useState<number | undefined>(undefined)

  // Si otra pestaña guardó una política nueva, el formulario se resincroniza
  // en lugar de seguir mostrando valores que ya no rigen.
  useEffect(() => {
    setMinimo(String(vigente.minimoRequerimientos))
    setMaximo(String(vigente.maximoRequerimientos))
    setExigeEntrega(vigente.exigeEntregaParaDerivar)
    setPorcentaje(String(vigente.porcentajeMinimoSorteo))
    setMaximoSemestre(String(vigente.maximoLegajosSemestre))
    setPlazos({ ...vigente.plazosPorCanal })
    setHabilitados(vigente.canalesHabilitados)
  }, [vigente])

  const incoherente = Number(minimo) > Number(maximo)
  const motivoCorto = motivo.trim().length < MINIMO_MOTIVO
  const sinCanales = habilitados.length === 0

  return (
    <form
      className="sigfisc-formulario"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (incoherente || motivoCorto || sinCanales) return
        guardar.mutate(
          {
            minimoRequerimientos: Number(minimo),
            maximoRequerimientos: Number(maximo),
            plazosPorCanal: plazos,
            canalesHabilitados: habilitados,
            exigeEntregaParaDerivar: exigeEntrega,
            porcentajeMinimoSorteo: Number(porcentaje),
            maximoLegajosSemestre: Number(maximoSemestre),
            motivo: motivo.trim(),
          },
          {
            onSuccess: (datos) => {
              setEnCurso(datos.consultasConPlazoEnCurso)
              setMotivo('')
              avisarExito('Política actualizada.')
            },
            onError: (error) => {
              avisarError(error, 'No se pudo guardar la política')
            },
          },
        )
      }}
    >
      <Callout tone="info" title={`Vigente desde ${fecha(vigente.vigenteDesde)}`}>
        Cada cambio se guarda como una versión nueva con su motivo, y queda auditado.
      </Callout>

      {enCurso !== undefined && enCurso > 0 && (
        <Callout tone="warning" title="Los plazos en curso no cambian">
          {plural(enCurso, 'consulta conserva', 'consultas conservan')} el plazo con el que se
          emitió su oficio. Lo nuevo aplica solo a las consultas que se abran desde ahora.
        </Callout>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Ciclo de requerimientos</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField
              id="minimo"
              label="Mínimo de requerimientos"
              required
              hint="Los que hay que agotar antes de poder derivar por no acreditado."
            >
              <Input
                type="number"
                min={1}
                max={20}
                value={minimo}
                onChange={(evento) => {
                  setMinimo(evento.target.value)
                }}
              />
            </FormField>

            <FormField
              id="maximo"
              label="Máximo de requerimientos"
              required
              hint="Alcanzado el tope, la única salida es derivar."
              {...(incoherente ? { error: 'El mínimo no puede superar al máximo.' } : {})}
            >
              <Input
                type="number"
                min={1}
                max={20}
                value={maximo}
                onChange={(evento) => {
                  setMaximo(evento.target.value)
                }}
              />
            </FormField>
          </div>

          <Casilla
            etiqueta="Exigir entrega efectiva antes de derivar por no acreditado"
            marcado={exigeEntrega}
            onCambio={setExigeEntrega}
            descripcion="Con esta casilla marcada no se remite al OCI un caso que nunca llegó a salir."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Selección de muestra</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          <p className="sigfisc-nota">
            Muestreo aleatorio simple sobre toda la población: sale el % de las altas del mes,
            redondeado hacia arriba, sin pasar de lo que quede del cupo del semestre. La Directiva
            N° 001-2023-SERVIR-GDSRH (numeral 7.3) fija al menos el 10 % y hasta 150 legajos por
            semestre.
          </p>
          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField id="porcentaje" label="% mínimo de la selección" required>
              <Input
                type="number"
                min={1}
                max={100}
                value={porcentaje}
                onChange={(evento) => {
                  setPorcentaje(evento.target.value)
                }}
              />
            </FormField>

            <FormField
              id="maximo-semestre"
              label="Máximo de legajos por semestre"
              required
              hint="Suma de las selecciones de enero a junio, o de julio a diciembre."
            >
              <Input
                type="number"
                min={1}
                max={100000}
                value={maximoSemestre}
                onChange={(evento) => {
                  setMaximoSemestre(evento.target.value)
                }}
              />
            </FormField>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Plazos por canal</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          {catalogos.canales.map((definicion) => (
            <FormField
              key={definicion.codigo}
              id={`plazo-${definicion.codigo}`}
              label={definicion.etiqueta}
              hint={
                definicion.plazoPorDefectoDias === 0
                  ? 'Canal síncrono: cero significa que no hay espera.'
                  : `Por defecto del canal: ${String(definicion.plazoPorDefectoDias)} días hábiles.`
              }
            >
              <Input
                type="number"
                min={0}
                max={120}
                value={String(plazos[definicion.codigo] ?? definicion.plazoPorDefectoDias)}
                onChange={(evento) => {
                  setPlazos((previo) => ({ ...previo, [definicion.codigo]: Number(evento.target.value) }))
                }}
              />
            </FormField>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Canales habilitados</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          <div className="sigfisc-conmutadores">
            {catalogos.canales.map((definicion) => (
              <Casilla
                key={definicion.codigo}
                etiqueta={definicion.etiqueta}
                marcado={habilitados.includes(definicion.codigo)}
                onCambio={(valor) => {
                  setHabilitados((previo) =>
                    valor
                      ? [...previo, definicion.codigo]
                      : previo.filter((canal) => canal !== definicion.codigo),
                  )
                }}
              />
            ))}
          </div>
          {sinCanales && (
            <Callout tone="critical" title="Debe quedar al menos un canal habilitado">
              Sin ninguno no se podría emitir ningún requerimiento.
            </Callout>
          )}
          <p className="sigfisc-nota">
            Deshabilitar un canal no borra el histórico: las consultas ya abiertas siguen su curso.
          </p>
        </CardContent>
      </Card>

      <FormField
        id="motivo-politica"
        label="Motivo del cambio"
        required
        hint={`Al menos ${String(MINIMO_MOTIVO)} caracteres. Se guarda junto con la versión anterior.`}
      >
        <Textarea
          rows={2}
          value={motivo}
          onChange={(evento) => {
            setMotivo(evento.target.value)
          }}
        />
      </FormField>

      <Button
        variant="primary"
        type="submit"
        loading={guardar.isPending}
        disabled={incoherente || motivoCorto || sinCanales}
      >
        Guardar la política
      </Button>
    </form>
  )
}
